import { useEffect, useState } from "react";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import type { ClaudeChannelMessage, PageContext } from "common";
import { classNames } from "@via/utils/helpers";
import { MarkdownMessage } from "@via/components/markdown/markdown-message";
import { AttachContextButton } from "./attach-context-button";

const formatElapsed = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

/** "Claude is working" with the time since the oldest message still queued. No timeout. */
const WorkingIndicator = ({ since }: { since: string }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <p
      data-testid="claude-working"
      className="flex items-center gap-2 self-start text-xs text-indigo-600">
      <span className="h-2 w-2 animate-pulse rounded-full bg-indigo-500" />
      Claude is working ({formatElapsed(now - new Date(since).getTime())})
    </p>
  );
};

interface ConversationViewProps {
  title: string;
  sessionLabel: string;
  messages: ClaudeChannelMessage[];
  sessionLive: boolean;
  onBack: () => void;
  /** Resolves null once the message was accepted, or with why it was not; the text stays until null. */
  onSend: (text: string, context?: PageContext) => Promise<string | null>;
}

export const ConversationView = ({
  title,
  sessionLabel,
  messages,
  sessionLive,
  onBack,
  onSend,
}: ConversationViewProps) => {
  const [text, setText] = useState("");
  const [attached, setAttached] = useState<PageContext | null>(null);
  const [sending, setSending] = useState(false);
  const [notSent, setNotSent] = useState<string | null>(null);

  const oldestQueued = messages.find(
    m => m.direction === "to_claude" && m.state === "queued"
  );

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || !sessionLive || sending) return;
    setSending(true);
    try {
      const refusal = await onSend(trimmed, attached ?? undefined);
      setNotSent(refusal);
      if (refusal === null) {
        setText("");
        setAttached(null);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2">
        <button
          type="button"
          aria-label="Back to sessions"
          onClick={onBack}
          className="rounded p-1 text-gray-500 hover:bg-gray-100">
          <ArrowLeftIcon className="h-4 w-4" />
        </button>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900">
            {title}
          </p>
          <p className="truncate text-xs text-gray-500">{sessionLabel}</p>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
        {messages.map(m =>
          m.kind === "status" ? (
            // Written by Foundry Connect, not by Claude: a deny that ended the turn (#1494).
            <p
              key={m.id}
              data-testid="claude-status"
              className="self-center px-4 text-center text-xs text-gray-500">
              {m.body.text}
            </p>
          ) : (
            <div
              key={m.id}
              data-testid="claude-message"
              className={classNames(
                "max-w-[85%] rounded-2xl px-4 py-2 text-sm shadow-sm",
                m.direction === "to_claude"
                  ? "self-end rounded-tr-sm bg-indigo-600 text-white"
                  : "self-start rounded-tl-sm bg-white text-gray-800 ring-1 ring-gray-200"
              )}>
              {m.direction === "from_claude" ? (
                <MarkdownMessage content={m.body.text} className="text-sm" />
              ) : (
                <p className="whitespace-pre-wrap break-words">{m.body.text}</p>
              )}
            </div>
          )
        )}
        {oldestQueued && <WorkingIndicator since={oldestQueued.createdAt} />}
      </div>

      <div className="border-t border-gray-200 bg-white/80 px-4 py-3">
        {!sessionLive && (
          <p className="mb-2 text-xs text-gray-500">
            This session has ended. The conversation stays readable here.
          </p>
        )}
        {notSent && (
          <p
            role="alert"
            data-testid="claude-not-sent"
            className="mb-2 text-xs text-red-700">
            {notSent}
          </p>
        )}
        <div className="mb-2">
          <AttachContextButton
            attached={attached}
            disabled={!sessionLive}
            onAttach={setAttached}
            onRemove={() => setAttached(null)}
          />
        </div>
        <div className="relative">
          <textarea
            aria-label="Message to Claude Code"
            value={text}
            disabled={!sessionLive || sending}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder={
              sessionLive
                ? "Message your Claude Code session..."
                : "Session ended"
            }
            rows={3}
            className="block w-full resize-none rounded-xl border-0 p-3 pr-14 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
          />
          <button
            type="button"
            aria-label="Send"
            onClick={() => void submit()}
            disabled={!sessionLive || sending || !text.trim()}
            className="absolute bottom-3 right-3 inline-flex items-center justify-center rounded-full bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
            ↑
          </button>
        </div>
      </div>
    </div>
  );
};
